import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaintenanceTypeApi } from '@api/maintenanceType.api';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { MaintenanceType } from 'app/core/interfaces';
import { debounceTime, tap } from 'rxjs/operators';
import english from '../../../../main/maintenances/i18n/en.json';
import spanish from '../../../../main/maintenances/i18n/es.json';
import portuguese from '../../../../main/maintenances/i18n/pt.json';

export function ValidateAutocomplete(): object {
  return { required: true };
}

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MaintenanceTypeSearchComponent),
  multi: true
};

@Component({
  selector: 'maintenance-type-search',
  templateUrl: './maintenance-type-search.component.html',
  styleUrls: ['./maintenance-type-search.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class MaintenanceTypeSearchComponent implements ControlValueAccessor, OnInit {
  @Input() public required: Boolean = false;
  @Input() public showAllAsAnOption: Boolean = false;
  @Input() public floatLabel: Boolean = true;
  @Input() public floatLabelText: string;
  @Input() public disabled: Boolean = false;
  @Input() public appearance = 'outline';
  public maintenanceTypes: MaintenanceType[] = [];
  public inputValue = new FormControl();
  public hasSelectedMaintenanceType: boolean;
  public selectedMaintenanceType: MaintenanceType;
  public loading = false;

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private maintenanceTypeApi: MaintenanceTypeApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
    if (this.required) {
      this.inputValue.setValidators(ValidateAutocomplete);
    }
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.inputValue.disable();
    }
    this.inputValue.valueChanges
      .pipe(
        tap((value) => {
          if (this.showAllAsAnOption && (value === 'allOptions')) {
            this.hasSelectedMaintenanceType = true;
          }
        }),
        debounceTime(500)
      )
      .subscribe((value) => {
        if (value) {
          this.searchMaintenanceTypes(value);
        }
      });
  }

  onChange: (_: any) => void = () => {};
  onTouched: (_: any) => void = () => {};

  searchMaintenanceTypes(value?: string): void {
    this.loading = true;
    this.maintenanceTypeApi.search({
      name_contains: value,
      archived: false
    }).subscribe((result) => {
      this.loading = false;
      this.maintenanceTypes = result.items;
    });
  }

  writeValue(maintenanceTypeId: string): void {
    if (maintenanceTypeId) {
      this.maintenanceTypeApi.searchOne(maintenanceTypeId).then((maintenanceType) => {
        this.selectedMaintenanceType = maintenanceType;
        this.hasSelectedMaintenanceType = true;
        this.inputValue.setErrors(null);
        this.inputValue.setValue(maintenanceTypeId);
      });
    } else if (!maintenanceTypeId && this.showAllAsAnOption) {
      this.inputValue.setValue('allOptions');
    } else {
      this.deselectMaintenanceType();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setSelectedMaintenanceType(): void {
    if (this.inputValue.value) {
      this.onChange(this.inputValue.value);
      this.selectedMaintenanceType = this.maintenanceTypes.find((maintenanceType) => maintenanceType.id === this.inputValue.value) as MaintenanceType;
      this.hasSelectedMaintenanceType = true;
      this.inputValue.setErrors(null);
    }
  }

  deselectMaintenanceType(): void {
    this.onChange(null);
    this.inputValue.setValue(null);
    this.selectedMaintenanceType = null;
    this.hasSelectedMaintenanceType = false;
    this.inputValue.setErrors(null);
  }
}
